import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { notificacionService, Notificacion } from '../../services/notificacionService';

interface NotificacionesPanelProps {
  onClose: () => void;
}

const NotificacionesPanel = ({ onClose }: NotificacionesPanelProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [categoriaFilter, setCategoriaFilter] = useState<string | undefined>(undefined);

  const { data, isLoading, refetch } = useQuery(
    ['notificaciones', categoriaFilter],
    () => notificacionService.getAll(false, categoriaFilter, 50)
  );

  const marcarComoLeidaMutation = useMutation(
    (id: number) => notificacionService.marcarComoLeida(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notificaciones']);
      }
    }
  );

  const marcarTodasComoLeidasMutation = useMutation(
    () => notificacionService.marcarTodasComoLeidas(),
    {
      onSuccess: () => {
        toast.success('Todas las notificaciones marcadas como leídas');
        queryClient.invalidateQueries(['notificaciones']);
      }
    }
  );

  const eliminarMutation = useMutation(
    (id: number) => notificacionService.eliminar(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notificaciones']);
      }
    }
  );

  // Refrescar cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);

    return () => clearInterval(interval);
  }, [refetch]);

  const handleClick = (notificacion: Notificacion) => {
    if (!notificacion.leida) {
      marcarComoLeidaMutation.mutate(notificacion.id);
    }
    
    if (notificacion.url) {
      navigate(notificacion.url);
      onClose();
    }
  };

  const handleEliminar = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (window.confirm('¿Eliminar esta notificación?')) {
      eliminarMutation.mutate(id);
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'success':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getCategoriaLabel = (categoria: string) => {
    const labels: Record<string, string> = {
      inventario: 'Inventario',
      orden: 'Orden',
      transferencia: 'Transferencia',
      sesion_caja: 'Sesión de Caja',
      cliente: 'Cliente',
      sistema: 'Sistema',
      otro: 'Otro'
    };
    return labels[categoria] || categoria;
  };

  const notificaciones = data?.data.notificaciones || [];
  const noLeidas = data?.data.noLeidas || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Notificaciones</h2>
            {noLeidas > 0 && (
              <span className="text-sm text-gray-600">{noLeidas} sin leer</span>
            )}
          </div>
          <div className="flex space-x-2">
            {noLeidas > 0 && (
              <button
                onClick={() => marcarTodasComoLeidasMutation.mutate()}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Marcar todas como leídas
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-4 border-b">
          <select
            value={categoriaFilter || ''}
            onChange={(e) => setCategoriaFilter(e.target.value || undefined)}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todas las categorías</option>
            <option value="inventario">Inventario</option>
            <option value="orden">Orden</option>
            <option value="transferencia">Transferencia</option>
            <option value="sesion_caja">Sesión de Caja</option>
            <option value="cliente">Cliente</option>
            <option value="sistema">Sistema</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : notificaciones.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay notificaciones
            </div>
          ) : (
            <div className="space-y-2">
              {notificaciones.map((notificacion) => (
                <div
                  key={notificacion.id}
                  onClick={() => handleClick(notificacion)}
                  className={`p-4 border rounded cursor-pointer hover:bg-gray-50 transition ${
                    notificacion.leida ? 'opacity-75' : 'border-l-4'
                  } ${!notificacion.leida ? getTipoColor(notificacion.tipo) : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold">{notificacion.titulo}</h3>
                        {!notificacion.leida && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                        )}
                        <span className="text-xs text-gray-500">
                          {getCategoriaLabel(notificacion.categoria)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{notificacion.mensaje}</p>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(notificacion.createdAt).toLocaleString('es-MX')}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleEliminar(e, notificacion.id)}
                      className="ml-2 text-gray-400 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificacionesPanel;

